/**
 * 分類単位分析アダプタ (Sprint BG-DataSettings 18、GAP U-L19 close)。
 *
 * 旧 `old/apps/user/src/lib/classificationAdapter.ts` を v2 仕様に踏襲。
 *
 * 役割:
 *   - 1 投稿の AI 解析結果 `classifications` 配列を ClassificationRow に展開
 *   - `classifications` を持たない **フラット構造** の解析結果 (ECW 系: category_codes /
 *     emotions / gql_code … がルート直下) も、M3 駆動で分類セットに正規化して展開
 *     (issue #33 モードドリブン化、2026-08-31)
 *   - M3 の分類軸を参照して汎用的に軸を解釈 (明示 classificationAxes → 無ければ fields[] から導出)
 *   - M3 未指定時は mode 別 fallback (walker → gql_code / bousai → hzr_code)
 *
 * 踏襲点:
 *   - 旧 `expandPostToClassificationRows` / `expandAllToClassificationRows` 構造
 *   - mode 別 root key 自動判別 (walker → ecw_w / bousai → classifications 直下)
 *   - reasoning field の dynamic 解決 (schema.reasoningField → fallback 'reasoning' → 'comment')
 *   - majorCode/majorName の自動抽出 ('before-hyphen' / 'first-char')
 *
 * 変更点 (v2):
 *   - 入力 `PostDoc` → `PostMinimal`、出力フィールド `postPhotoUrl` 等は PostMinimal が
 *     photoPath/photoThumbPath のみ持つため、本アダプタは photoUrl 解決は呼出側責務に変更
 *   - `EcwPostData` → 不要 (PostMinimal の location / createdAt から直接構築)
 *   - 戻り値 ClassificationRow は本ファイルで型定義 (shared-types に未昇格のため local 型)
 *
 * 廃止点:
 *   - 旧 `outputSchema` (AiOutputSchema、v1 命名) → v2 では `AiOutputFormatSchema` に統合
 *   - 旧 `postHappinessScore` / `postHealthScore` 直接フィールド (Phase 3.5 で extraFields 一本化)
 *
 * 関連 docs: docs/91 U-L19 (classification 展開)、docs/04 §3-1 M3。
 */
import {
  classificationsFromFlatResult,
  resolveClassificationsKey,
  resolveFormatAxes,
  splitCodeValue,
  type AiOutputFormat,
  type AiOutputFormatSchema,
  type DistrictMode,
} from '@citizen-walk/shared-types';
import type { PostMinimal } from '../posts';

/**
 * 旧 ClassificationRow の v2 minimum 型 (旧の shared-types から transplant)。
 * Phase 5 後続で shared-types に昇格予定。
 */
export interface ClassificationRow {
  rank: number;
  axes: Record<
    string,
    {
      /** 代表値 (codes[0])。chip 表示・従来互換の単一値。 */
      code: string;
      /**
       * その軸が持つ **全ての値**。感情 / 対象コード / ゲール基準のように 1 投稿で複数を
       * 並べる軸があるため、代表値だけでなく列挙全体を保持する。マトリクスの
       * 「AI 認識物単位」表示では `expandRowsByDisplayedAxes` がこれを行に展開する。
       */
      codes: string[];
      name: string;
      majorCode?: string;
      majorName?: string;
      sub?: string[];
    }
  >;
  assessment: string;
  assessmentLevel: string;
  score: number;
  reasoning: string;
  postId: string;
  postTitle: string;
  postPhotoPath: string | null;
  postPhotoThumbPath: string | null;
  postLocation: string;
  postAuthor: string;
  postSessionId: string | null;
  /**
   * `expandRowsByDisplayedAxes` が列挙値ごとに複製した行を識別する suffix。
   * 同じ (postId, rank) の複製どうしが同一 id にならないよう `clsRowToPost` の id に足す
   * (2 値が同じバケットに落ちるケースで React key が重複するのを防ぐ)。
   */
  variantKey?: string;
}

/** 分類コードから大分類部分を抽出 (majorCodeExtract 駆動)。 */
function extractMajor(
  code: string,
  majorMode: 'before-hyphen' | 'first-char' | undefined,
): string | undefined {
  if (majorMode === 'before-hyphen' && code.includes('-')) return code.split('-')[0];
  if (majorMode === 'first-char' && code.length > 0) return code[0];
  return undefined;
}

/**
 * 表示中の軸に並んだ複数値を、行 (= AI 認識物) に展開する。
 *
 * マトリクスの「AI 認識物単位」では 1 行 = 1 マス要素なので、感情や対象コードのように
 * 1 投稿で複数を列挙する軸を代表値だけで扱うと、2 件目以降がどのセルにも現れない。
 * **縦軸 / 横軸に選ばれている軸** についてのみ列挙値の直積へ展開し、それぞれを別の
 * データとして適切なセルに置けるようにする (表示されていない軸は代表値のまま。
 * 見えない軸で件数が水増しされるのを避けるため)。
 *
 * @param axisKeys 展開対象の軸 key (`ClassificationRow.axes` の key、重複・未知は無視)
 */
export function expandRowsByDisplayedAxes(
  rows: ClassificationRow[],
  axisKeys: (string | null | undefined)[],
): ClassificationRow[] {
  const targets = [...new Set(axisKeys.filter((k): k is string => !!k))];
  if (targets.length === 0) return rows;
  const out: ClassificationRow[] = [];
  for (const row of rows) {
    let variants: ClassificationRow[] = [row];
    for (const key of targets) {
      const axis = row.axes[key];
      const codes = axis?.codes ?? [];
      if (!axis || codes.length <= 1) continue;
      variants = variants.flatMap((v) =>
        codes.map((code) => ({
          ...v,
          variantKey: v.variantKey ? `${v.variantKey}_${code}` : code,
          axes: { ...v.axes, [key]: { ...axis, code, codes: [code] } },
        })),
      );
    }
    out.push(...variants);
  }
  return out;
}

/**
 * 1 投稿から ClassificationRow[] を生成 (旧 `expandPostToClassificationRows` 踏襲)。
 */
export function expandPostToClassificationRows(
  post: PostMinimal,
  format: AiOutputFormat | null | undefined,
  mode?: DistrictMode,
): ClassificationRow[] {
  if (post.aiAnalysisStatus !== 'done') return [];

  const schema = format?.schema ?? null;
  const classifications = extractClassifications(post, schema, mode);
  if (!classifications || classifications.length === 0) return [];
  // 分類セットに含まれない軸 (投稿単位のフラットな値) を引くためのルート。
  const rootResult = (post.aiAnalysisGeneric ?? null) as Record<string, unknown> | null;

  const reasoningField = schema?.reasoningField ?? 'reasoning';

  // 軸は M3 駆動 (モード非依存)。classificationAxes が空でも fields[] から導出する
  // (issue #33 モードドリブン化)。導出できないときだけ下の mode 別 fallback に落ちる。
  const resolvedAxes = resolveFormatAxes(schema);

  return classifications.map((cls): ClassificationRow => {
    const axes: ClassificationRow['axes'] = {};

    if (schema?.classificationAxes || resolvedAxes.length > 0) {
      for (const { axis: axisDef, separator } of resolvedAxes) {
        // 1 セルに複数値が並ぶ軸 (感情 / 対象コード / ゲール基準…) は列挙全体を保持し、
        // 代表値は先頭とする。フラット構造・object-array のどちらでも同じ扱いになる。
        // 分類行に値が無い軸は、ルート (投稿単位のフラットな値) から引く。
        // 「ゲール基準だけを分類セットにし、対象物 / 感情は投稿単位のまま」といった
        // 混在フォーマットでも、対象物 × 感情の交差集計が保てるようにするため。
        const rawValue = cls[axisDef.codeField] ?? rootResult?.[axisDef.codeField];
        const codes = splitCodeValue(rawValue, separator);
        const code = codes[0] ?? '';
        const name = String(cls[axisDef.nameField] ?? '');
        const subRaw = axisDef.subField ? cls[axisDef.subField] : undefined;
        const sub = Array.isArray(subRaw) ? subRaw.map(String) : [];

        const key = (axisDef as { key?: string }).key ?? axisDef.codeField;
        const majorMode = (axisDef as { majorCodeExtract?: 'before-hyphen' | 'first-char' })
          .majorCodeExtract;
        axes[key] = { code, codes, name, majorCode: extractMajor(code, majorMode), sub };
      }
    } else {
      // 【廃止予定 fallback】M3 未指定時の mode 別軸抽出 (旧踏襲、モードドリブン化 MD-4、2026-07-24)。
      // gql_*/hzr_* の固定フィールド名は legacy 地区の mode 別 result 専用。新規地区は
      // M3 classificationAxes (上の分岐) で任意軸を解決する。撤去条件: M3 未設定地区 0 件。
      const categoryCodes = splitCodeValue(cls.category_code);
      axes.category = {
        code: categoryCodes[0] ?? '',
        codes: categoryCodes,
        name: String(cls.category_name ?? ''),
        majorCode: (categoryCodes[0] ?? '').split('-')[0] || undefined,
      };
      if (mode === 'walker' || cls.gql_code) {
        const gqlCodes = splitCodeValue(cls.gql_code);
        axes.gql = {
          code: gqlCodes[0] ?? '',
          codes: gqlCodes,
          name: String(cls.gql_name ?? ''),
          sub: (cls.sub_gql as string[]) ?? [],
        };
      }
      if (mode === 'bousai' || cls.hzr_code) {
        const hzrCodes = splitCodeValue(cls.hzr_code);
        axes.hzr = {
          code: hzrCodes[0] ?? '',
          codes: hzrCodes,
          name: String(cls.hzr_name ?? ''),
          sub: (cls.sub_hzr as string[]) ?? [],
        };
      }
    }

    return {
      rank: Number(cls.rank ?? 0),
      axes,
      assessment: String(cls.assessment ?? ''),
      assessmentLevel: String(cls.assessment_level ?? ''),
      score: Number(cls.score ?? 0),
      reasoning: String(cls[reasoningField] ?? cls.reasoning ?? cls.comment ?? ''),
      postId: post.id,
      postTitle: post.title,
      postPhotoPath: post.photoPath,
      postPhotoThumbPath: post.photoThumbPath,
      postLocation: post.location ? `${post.location.latitude},${post.location.longitude}` : '',
      postAuthor: post.authorNickname,
      postSessionId: post.sessionId,
    };
  });
}

/** 全投稿を一括展開 (旧 `expandAllToClassificationRows` 踏襲)。 */
export function expandAllToClassificationRows(
  posts: PostMinimal[],
  format: AiOutputFormat | null | undefined,
  mode?: DistrictMode,
): ClassificationRow[] {
  const rows: ClassificationRow[] = [];
  for (const p of posts) {
    rows.push(...expandPostToClassificationRows(p, format, mode));
  }
  return rows;
}

/**
 * AI 解析結果から classifications 配列を取り出す (旧 extractClassifications 踏襲)。
 * v2: aiAnalysisBousai / aiAnalysisWalker / aiAnalysisEcw / aiAnalysisRaw.parsed を順に試す。
 */
function extractClassifications(
  post: PostMinimal,
  schema: AiOutputFormatSchema | null | undefined,
  mode?: DistrictMode,
): Record<string, unknown>[] | null {
  // old2 整合 (列名動的化レベル3): 汎用 payload を最優先 (Phase 2 writeback)。
  // fields[] 駆動の generic 出力では classifications が aiAnalysisGeneric に入るため、
  // mode 別 result より先に参照しないと cls データソース (matrix の AI 認識物単位) が空になる。
  // old2 `lib/classificationAdapter.ts::extractClassifications` (L121-130) 踏襲。
  // 分類セットの key は `classifications` 固定ではなく出力フォーマットの fields[] から引く
  // (admin が object-array に任意のキー名を付けられるため)。
  const clsKey = resolveClassificationsKey(schema);
  const generic = post.aiAnalysisGeneric as Record<string, unknown> | null;
  const genericCls = generic?.[clsKey];
  if (Array.isArray(genericCls) && genericCls.length > 0) {
    return genericCls as Record<string, unknown>[];
  }
  // issue #33 モードドリブン化 (2026-08-31): classifications[] を持たない **フラット構造**
  // (ECW 系: category_codes / emotions / gql_code … がルート直下に並ぶ) を、M3 の
  // fields[] / classificationAxes 駆動で分類セットへ正規化する。これが無いと当該投稿は
  // マトリクス・一覧・地図の「AI 認識物単位」に 1 行も現れない (丸ごと欠落する)。
  if (generic) {
    const flatRows = classificationsFromFlatResult(generic, schema);
    if (flatRows.length > 0) return flatRows;
  }
  // 従来どおり: 空の分類セットを持つ投稿は「分類なし」で確定 (旧方式の地区は不変)。
  if (Array.isArray(genericCls)) {
    return genericCls as Record<string, unknown>[];
  }
  // 【廃止予定 fallback】以下の mode 別 result 参照は legacy 地区 (legacyModeResults) の
  // 投稿のみ (モードドリブン化 MD-4、2026-07-24)。新規地区は上の generic で解決される。
  // bousai: aiAnalysisBousai.classifications
  if (mode === 'bousai' && post.aiAnalysisBousai?.classifications) {
    return post.aiAnalysisBousai.classifications as unknown as Record<string, unknown>[];
  }
  if (post.aiAnalysisBousai?.classifications && !mode) {
    return post.aiAnalysisBousai.classifications as unknown as Record<string, unknown>[];
  }
  // walker: aiAnalysisWalker.classifications (旧は ecw_w nested、v2 PostMinimal では flat 化済)
  if (post.aiAnalysisWalker?.classifications) {
    return post.aiAnalysisWalker.classifications as unknown as Record<string, unknown>[];
  }
  // rawOutput.parsed.classifications (rootKey 対応)
  if (post.aiAnalysisRaw?.parsed && typeof post.aiAnalysisRaw.parsed === 'object') {
    const parsed = post.aiAnalysisRaw.parsed as Record<string, unknown>;
    if (parsed[clsKey]) return parsed[clsKey] as Record<string, unknown>[];
    if (schema?.rootKey) {
      const nested = parsed[schema.rootKey] as Record<string, unknown> | undefined;
      if (nested?.[clsKey]) return nested[clsKey] as Record<string, unknown>[];
    }
  }
  return null;
}
